import store from '@/store';

import { dedupeTags } from '@/scripts/tagText.mjs';

export function deepEqual(object1, object2) {
    return JSON.stringify(object1) === JSON.stringify(object2);
}

export function sortByName(element1, element2) {
    const name1 = element1.name.toLowerCase();
    const name2 = element2.name.toLowerCase();

    if (name1 > name2) {
        return 1;
    }
    if (name1 < name2) {
        return -1;
    }
    return 0;
}

export function sortFilterByName(filters) {
    if (filters == null) return;

    filters.forEach((filter) => {
        if (filter.children_filter != null) {
            sortFilterByName(filter.children_filter);
        } else {
            filter.filter_items.sort((a, b) => sortByName(a, b));
        }
    });
}

export function sortProviderAndGameByName(providers) {
    providers.sort((a, b) => sortByName(a, b));
    providers.forEach((provider) => {
        provider.game_name.sort((a, b) => sortByName(a, b));
    });
}

export function sortUser(element1, element2, fieldName, isDesc = false) {
    let elem1;
    let elem2;
    if (typeof element1[fieldName] === 'string') {
        elem1 = element1[fieldName].toLowerCase();
        elem2 = element2[fieldName].toLowerCase();
    } else {
        elem1 = element1[fieldName];
        elem2 = element2[fieldName];
    }

    if (typeof elem1 === 'boolean') {
        if (elem1 === true && elem2 === false) {
            return isDesc === true ? 1 : -1;
        }
        if (elem1 === false && elem2 === true) {
            return isDesc === true ? -1 : 1;
        }
    } else {
        if (elem1 > elem2) {
            return isDesc === true ? -1 : 1;
        }
        if (elem1 < elem2) {
            return isDesc === true ? 1 : -1;
        }
    }
    return 0;
}

export async function convertToFormData(convertObject) {
    return Object.keys(convertObject)
        .map((key) => `${key}=${encodeURIComponent(convertObject[key])}`)
        .join('&');
}

export function groupByName(list) {
    if (list == null || Object.keys(list).length === 0) {
        return {};
    }

    const group = {};

    list.forEach((listValue, listIndex) => {
        const firstSymbol = listValue.name[0].toUpperCase();
        if (group[firstSymbol] == null) {
            group[firstSymbol] = [];
        }
        group[firstSymbol].push(listValue);
    });

    const sortedKeys = Object.keys(group).sort();
    const sortedGroup = {};
    sortedKeys.forEach((key) => {
        sortedGroup[key] = group[key];
    });

    return sortedGroup;
}

/**
 * Format a date string to Minsk timezone (UTC+3)
 * @param {string} dateString - ISO date string
 * @param {boolean} includeTime - Whether to include time in the output
 * @returns {string} Formatted date string (DD.MM.YYYY or DD.MM.YYYY HH:MM)
 */
export function formatMinskDate(dateString, includeTime = false) {
    if (!dateString) return 'N/A';

    // Ensure UTC parsing if no timezone specified
    let date;
    if (
        dateString.endsWith('Z') ||
        dateString.includes('+') ||
        dateString.includes('-', 10)
    ) {
        date = new Date(dateString);
    } else {
        date = new Date(dateString + 'Z');
    }

    if (isNaN(date.getTime())) return 'N/A';

    // Convert to Minsk timezone (UTC+3)
    const minskOffset = 3 * 60;
    const minskTime = new Date(date.getTime() + minskOffset * 60 * 1000);

    const day = String(minskTime.getUTCDate()).padStart(2, '0');
    const month = String(minskTime.getUTCMonth() + 1).padStart(2, '0');
    const year = minskTime.getUTCFullYear();

    if (!includeTime) {
        return `${day}.${month}.${year}`;
    }

    const hours = String(minskTime.getUTCHours()).padStart(2, '0');
    const minutes = String(minskTime.getUTCMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
}

export function filterNoChild(filterList) {
    const noChildFilter = [];
    filterList.forEach((filter) => {
        if (filter.children_filter === null) noChildFilter.push(filter);
        else noChildFilter.push(...filter.children_filter);
    });
    return noChildFilter;
}

export function filterItem(filterList) {
    const filterItemList = [];
    filterList.forEach((filter) => {
        if (filter.children_filter == null) {
            filterItemList.push(...filter.filter_items);
        } else {
            filterItemList.push(...filterItem(filter.children_filter));
        }
    });
    return filterItemList;
}

export function searchFilterPath(filterList, filterID) {
    const path = {
        filter: null,
        filterIndex: null,
    };

    let filterIndex = filterList.map((filter) => filter.id).indexOf(filterID);
    if (filterIndex >= 0) {
        path.filterIndex = filterIndex;
    } else {
        for (let i = 0; i < filterList.length; i++) {
            if (filterList[i].children_filter != null) {
                const childPath = searchFilterPath(
                    filterList[i].children_filter,
                    filterID
                );
                if (childPath.filterIndex != null) {
                    path.filter = i;
                    path.filterIndex = childPath.filterIndex;
                    break;
                }
            }
        }
    }

    return path;
}

export function searchFilterItemPath(filterList, filterItemID) {
    const path = {
        filter: null,
        childFilter: null,
        filterIndex: null,
    };

    for (let i = 0; i < filterList.length; i++) {
        if (filterList[i].filter_items.length > 0) {
            const filterItemIndex = filterList[i].filter_items
                .map((filterItem) => filterItem.id)
                .indexOf(filterItemID);
            if (filterItemIndex >= 0) {
                path.filter = i;
                path.filterIndex = filterItemIndex;
                break;
            }
        } else if (filterList[i].children_filter != null) {
            const subPath = searchFilterItemPath(
                filterList[i].children_filter,
                filterItemID
            );
            if (subPath.filter != null) {
                path.filter = i;
                path.childFilter = subPath.filter;
                path.filterIndex = subPath.filterIndex;
                break;
            }
        }
    }

    return path;
}

export async function clearText(clearingText) {
    // Delegates to the pure, import-free dedupeTags (src/scripts/tagText.mjs) so the
    // normalization is unit-testable standalone. Now also collapses singular<->plural
    // duplicates and keeps the capitalized variant (e.g. "Egypt" over "egypt").
    return dedupeTags(clearingText);
}

export async function comparingArrays(firstArray, secondArray) {
    if (firstArray.length !== secondArray.length) {
        return false;
    }

    const sortFirstArray = structuredClone(firstArray).sort();
    const sortSecondArray = structuredClone(secondArray).sort();

    if (
        sortFirstArray.every(
            (element, index) => element === sortSecondArray[index]
        )
    ) {
        return true;
    }

    return false;
}

export async function changeData(
    event,
    fieldName,
    commit,
    dispatch,
    isCheckBox = false,
    isSetValue = false
) {
    const data =
        isSetValue === true
            ? event
            : isCheckBox === true
            ? event.target.checked
            : event.target.value;
    store.commit(commit, {
        fieldName: fieldName,
        fieldValue: data,
    });
    return await store.dispatch(dispatch);
}

/**
 * Capitalize first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} String with first letter capitalized
 */
export function capitalizeFirst(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Format ISO timestamp to Minsk timezone (UTC+3)
 * @param {string} isoString - ISO 8601 timestamp from backend (UTC without Z suffix)
 * @returns {string} Formatted date in Minsk timezone: DD.MM.YYYY HH:MM
 */
export function formatMinskTimestamp(isoString) {
    if (!isoString) return 'N/A';

    // Backend sends UTC time without 'Z' suffix, so add it to ensure correct parsing
    const utcString = isoString.endsWith('Z') ? isoString : isoString + 'Z';
    const utcDate = new Date(utcString);
    const MINSK_OFFSET_MS = 3 * 60 * 60 * 1000; // UTC+3 in milliseconds
    const minskTime = new Date(utcDate.getTime() + MINSK_OFFSET_MS);

    // Use UTC methods to avoid browser timezone interference
    const day = String(minskTime.getUTCDate()).padStart(2, '0');
    const month = String(minskTime.getUTCMonth() + 1).padStart(2, '0');
    const year = minskTime.getUTCFullYear();
    const hours = String(minskTime.getUTCHours()).padStart(2, '0');
    const minutes = String(minskTime.getUTCMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
}
